/**
 * Owner: Masa Bayu
 * File: QxWriterEditorBody.tsx
 * Date:Time: 2026-10-07 15:47
 * Description: EM-FIX-01 · Edit absolute · body ids for justify paint.
 */

import { useEffect, useMemo, type ReactElement } from 'react';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { loadStructuredPageIntoEditor } from './load-structured-page';
import {
  AbsolutePlacementPlugin,
  textAtomsForEditPlacement,
} from './absolute-placement-plugin';
import type { StructuredPage } from '../types';
import './qxw-theme.css';

export interface QxWriterEditorBodyProps {
  readonly page: StructuredPage;
  /** EM-FIX-01 · paint paragraphs at witnessed bbox. */
  readonly absolutePlacement?: boolean;
}

function EditorErrorBoundary(props: {
  readonly children: ReactElement;
  readonly onError: (error: Error) => void;
}): ReactElement {
  return (
    <LexicalErrorBoundary onError={props.onError}>
      {props.children}
    </LexicalErrorBoundary>
  );
}

/**
 * Consumer context SAHAJA — tiada LexicalComposer di sini.
 * Composer dimiliki oleh QxWriterEditor (standalone) atau QxWorkspace (shell).
 */
export function QxWriterEditorBody(
  props: QxWriterEditorBodyProps,
): ReactElement {
  const { page, absolutePlacement = false } = props;
  const [editor] = useLexicalComposerContext();
  const atoms = useMemo(
    () => textAtomsForEditPlacement(page),
    [page],
  );
  const bodyAtomIds = useMemo(
    () =>
      new Set(
        page.bodyLayer
          .filter((a) => a.type === 'text')
          .map((a) => a.id),
      ),
    [page],
  );

  useEffect(() => {
    loadStructuredPageIntoEditor(editor, page);
  }, [editor, page]);

  const editableClass = absolutePlacement
    ? 'qxw-contenteditable qxw-contenteditable--absolute'
    : 'qxw-contenteditable';

  const editableStyle = absolutePlacement
    ? {
        position: 'relative' as const,
        width: '100%',
        height: '100%',
        minHeight: '100%',
        outline: 'none',
      }
    : undefined;

  return (
    <>
      <RichTextPlugin
        contentEditable={
          <ContentEditable
            className={editableClass}
            style={editableStyle}
          />
        }
        placeholder={<div className="qxw-placeholder">Mula menaip…</div>}
        ErrorBoundary={EditorErrorBoundary}
      />
      <HistoryPlugin />
      {absolutePlacement ? (
        <AbsolutePlacementPlugin
          atoms={atoms}
          bodyAtomIds={bodyAtomIds}
        />
      ) : null}
    </>
  );
}
